import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { dbSelect, dbInsert } from '../lib/api';
import { VISIBLE_AGENTIC_CODER_MODELS, type VisibleAgenticCoderModelId } from '../lib/agentModels';
import { ArrowLeft, RefreshCw, CheckCircle, Wrench, Clock, Eye, ChevronDown, Check } from 'lucide-react';
import type { Conversation, Message, CodeValidation } from '../lib/supabase';

const ADMIN_EMAIL = 'a.burkov@gmail.com';
const PENDING_AGENT_RUN_PREFIX = 'llambada.pending-agent-run.';

const BASE_validationMessage =
  'Review all code files in this project against the coding instructions. Check every rule and guideline carefully. If you find any misalignments, violations, or issues, fix them. If everything is already compliant with all instructions, respond with exactly the single word: NO_CHANGE_NEEDED';

function buildValidationMessage(extraConstraints: string): string {
  const trimmed = extraConstraints.trim();
  if (!trimmed) return BASE_validationMessage;
  return `${BASE_validationMessage}\n\nIn particular, verify whether the following constraints are respected: ${trimmed}\n\nFix all constraint violations.`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectRow {
  id: string;
  name: string;
  logo_emoji: string | null;
  cloud_run_job_name: string | null;
  profiles: { display_name: string | null; email: string } | null;
}

interface ValidationState {
  status: 'idle' | 'creating' | 'streaming' | 'done' | 'error';
  streamingStatus: string;
  streamingContent: string;
  conversationId: string | null;
  projectId: string;
  result: 'no_change_needed' | 'fixed' | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Standalone SSE streaming function (simplified from useApi.ts sendMessage)
// ---------------------------------------------------------------------------

async function runValidationStream(params: {
  accessToken: string;
  conversationId: string;
  userMessageId: string;
  message: string;
  model: string;
  onStatus: (status: string) => void;
  onContent: (fullContent: string) => void;
  signal?: AbortSignal;
}): Promise<{ fullContent: string }> {
  const { accessToken, conversationId, userMessageId, message, model, onStatus, onContent, signal } = params;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Step 1: Auth
  onStatus('Authenticating...');
  const authResponse = await fetch(`${supabaseUrl}/functions/v1/agentic-coder-auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      conversationId,
      message,
      currentUserMessageId: userMessageId,
      model,
    }),
    signal,
  });

  if (!authResponse.ok) {
    const errorData = await authResponse.json();
    throw new Error(errorData.error || 'Failed to authenticate');
  }

  const { token, serviceUrl, kimiServiceUrl, glmServiceUrl, minimaxServiceUrl, isFirstMessage, isForkedProject, codeZipBase64, codeZipGcsPath, projectId } =
    await authResponse.json();

  // Pick the right service URL based on model provider
  const targetServiceUrl = model.startsWith('glm-')
    ? (glmServiceUrl || serviceUrl)
    : model.startsWith('kimi-')
      ? (kimiServiceUrl || serviceUrl)
      : model.startsWith('minimax-')
        ? (minimaxServiceUrl || serviceUrl)
        : serviceUrl;

  // Step 2: Call Cloud Run /run
  onStatus('Connecting to service...');
  const serviceResponse = await fetch(`${targetServiceUrl}/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      message,
      conversationId,
      currentUserMessageId: userMessageId,
      projectId,
      isFirstMessage,
      isForkedProject,
      codeZipBase64,
      codeZipGcsPath,
      model,
    }),
    signal,
  });

  if (!serviceResponse.ok) {
    throw new Error('Failed to connect to service');
  }

  // Step 3: Parse SSE stream
  const reader = serviceResponse.body?.getReader();
  if (!reader) throw new Error('No response stream');

  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  let currentEventType = '';

  const processSseBlock = (rawBlock: string) => {
    const lines = rawBlock.split('\n');
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          switch (currentEventType) {
            case 'status':
              onStatus(data.message || '');
              break;
            case 'text':
              fullContent += data.content + '\n';
              onContent(fullContent);
              break;
            case 'tool':
              onStatus(`Using tool: ${data.name}`);
              break;
            case 'error':
              throw new Error(data.message || 'An error occurred');
            case 'complete':
              break;
          }
        } catch (err) {
          if (err instanceof Error && err.message !== 'An error occurred') {
            // Re-throw actual errors from the 'error' event
            if (currentEventType === 'error') throw err;
          }
          // Ignore JSON parse errors for incomplete data
        }
      }
    }
    currentEventType = '';
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() || '';
    for (const block of blocks) {
      processSseBlock(block);
    }
  }

  if (buffer.trim()) {
    processSseBlock(buffer);
  }

  return { fullContent };
}

function detectValidationResult(content: string): 'no_change_needed' | 'fixed' {
  return content.includes('NO_CHANGE_NEEDED') ? 'no_change_needed' : 'fixed';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminCodeRefreshPage() {
  const { user, session, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [validations, setValidations] = useState<Map<string, CodeValidation>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationStates, setValidationStates] = useState<Map<string, ValidationState>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const [selectedModel, setSelectedModel] = useState<VisibleAgenticCoderModelId>('claude-opus-4-6');
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [extraConstraints, setExtraConstraints] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Code Refresh - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);

    try {
      // Fetch projects first (always works), then validations (may fail if table doesn't exist yet)
      const projectsResult = await dbSelect<ProjectRow[]>('projects', {
        token: session.access_token,
        select: 'id,name,logo_emoji,cloud_run_job_name,profiles!projects_user_id_fkey(display_name,email)',
        filters: { cloud_run_job_name: 'not.is.null', is_published: 'eq.true', forked_from: 'is.null' },
        order: 'created_at.desc',
      });

      if (projectsResult.error) throw projectsResult.error;

      // Normalize profiles join (may come as array)
      const normalizedProjects = (projectsResult.data || []).map((p) => ({
        ...p,
        profiles: Array.isArray((p as any).profiles)
          ? (p as any).profiles[0] || null
          : (p as any).profiles,
      }));
      setProjects(normalizedProjects);

      // Fetch validations separately — table may not exist yet
      const validationsResult = await dbSelect<CodeValidation[]>('code_validations', {
        token: session.access_token,
        order: 'validated_at.desc',
      });

      const valMap = new Map<string, CodeValidation>();
      if (!validationsResult.error) {
        for (const v of validationsResult.data || []) {
          if (!valMap.has(v.project_id)) {
            valMap.set(v.project_id, v);
          }
        }
      }
      setValidations(valMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      fetchData();
    }
  }, [user, fetchData]);

  const updateValidationState = (projectId: string, updates: Partial<ValidationState>) => {
    setValidationStates((prev) => {
      const next = new Map(prev);
      const current = next.get(projectId) || {
        status: 'idle' as const,
        streamingStatus: '',
        streamingContent: '',
        conversationId: null,
        projectId,
        result: null,
        error: null,
      };
      next.set(projectId, { ...current, ...updates });
      return next;
    });
  };

  const handleRefresh = async (projectId: string) => {
    if (!session) return;

    const validationMessage = buildValidationMessage(extraConstraints);
    const abortController = new AbortController();
    abortControllersRef.current.set(projectId, abortController);
    let pendingKey: string | null = null;

    updateValidationState(projectId, {
      status: 'creating',
      streamingStatus: 'Creating conversation...',
      streamingContent: '',
      conversationId: null,
      result: null,
      error: null,
    });

    try {
      // 1. Create conversation
      const { data: conversation, error: convError } = await dbInsert<Conversation>('conversations', {
        project_id: projectId,
        title: 'Code Validation',
      }, { token: session.access_token });

      if (convError || !conversation) throw new Error(convError?.message || 'Failed to create conversation');

      const conversationId = conversation.id;
      updateValidationState(projectId, { conversationId, status: 'streaming' });

      // 2. Persist user message
      const { data: userMessage, error: msgError } = await dbInsert<Message>('messages', {
        conversation_id: conversationId,
        role: 'user',
        content: validationMessage,
      }, { token: session.access_token });

      if (msgError || !userMessage) throw new Error(msgError?.message || 'Failed to save message');

      // Write pending-agent-run so ConversationPage can recover/show the stream
      pendingKey = `${PENDING_AGENT_RUN_PREFIX}${conversationId}`;
      try {
        localStorage.setItem(pendingKey, JSON.stringify({
          conversationId,
          userMessageId: userMessage.id,
          startedAt: new Date().toISOString(),
        }));
      } catch { /* ignore */ }

      // 3. Run validation stream
      const { fullContent } = await runValidationStream({
        accessToken: session.access_token,
        conversationId,
        userMessageId: userMessage.id,
        message: validationMessage,
        model: selectedModel,
        onStatus: (status) => updateValidationState(projectId, { streamingStatus: status }),
        onContent: (content) => {
          updateValidationState(projectId, { streamingContent: content });
          // Keep pending-agent-run updated so ConversationPage recovery can show partial content
          try {
            localStorage.setItem(pendingKey!, JSON.stringify({
              conversationId,
              userMessageId: userMessage.id,
              startedAt: new Date().toISOString(),
              partialAssistantContent: content,
            }));
          } catch { /* ignore */ }
        },
        signal: abortController.signal,
      });

      // 4. Persist assistant message and clear pending-agent-run
      const trimmed = fullContent.trim();
      if (trimmed) {
        await dbInsert<Message>('messages', {
          conversation_id: conversationId,
          role: 'assistant',
          content: trimmed,
        }, { token: session.access_token });
      }
      try { localStorage.removeItem(pendingKey); } catch { /* ignore */ }

      // 5. Detect result and persist validation
      const result = detectValidationResult(fullContent);
      const now = new Date().toISOString();

      await dbInsert<CodeValidation>('code_validations', {
        project_id: projectId,
        conversation_id: conversationId,
        validation_result: result,
        validated_at: now,
      }, { token: session.access_token });

      // Update local state
      setValidations((prev) => {
        const next = new Map(prev);
        next.set(projectId, {
          id: '',
          project_id: projectId,
          conversation_id: conversationId,
          validation_result: result,
          validated_at: now,
          created_at: now,
        });
        return next;
      });

      updateValidationState(projectId, {
        status: 'done',
        result,
        streamingStatus: result === 'no_change_needed' ? 'No changes needed' : 'Code was fixed',
      });
    } catch (err) {
      if ((err as any)?.name === 'AbortError') {
        updateValidationState(projectId, { status: 'idle', streamingStatus: '' });
      } else {
        updateValidationState(projectId, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Validation failed',
          streamingStatus: '',
        });
      }
    } finally {
      abortControllersRef.current.delete(projectId);
      // Clear pending-agent-run on error/abort (success already clears it above)
      if (pendingKey) {
        try { localStorage.removeItem(pendingKey); } catch { /* ignore */ }
      }
    }
  };

  if (authLoading) return <PageLoader />;
  if (!user || user.email !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-500/10 text-teal-500">
            <RefreshCw className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-theme-text">Code Refresh</h1>
            <p className="text-theme-text-secondary text-sm">
              Validate mini-app code against platform instructions and auto-fix misalignments.
            </p>
          </div>
          {/* Model selector dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 h-10 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors"
              title="Select coder agent"
            >
              <span className="text-sm font-medium text-theme-text whitespace-nowrap">
                {VISIBLE_AGENTIC_CODER_MODELS.find(m => m.id === selectedModel)?.name.replace('Claude ', '') || selectedModel}
              </span>
              <ChevronDown className={`h-4 w-4 text-theme-text-muted transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {modelDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                {VISIBLE_AGENTIC_CODER_MODELS.map((model) => (
                  <button
                    key={model.id}
                    type="button"
                    onClick={() => {
                      setSelectedModel(model.id);
                      setModelDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-theme-surface-hover transition-colors ${
                      selectedModel === model.id ? 'bg-theme-surface-hover' : ''
                    }`}
                  >
                    <div>
                      <div className="text-sm font-medium text-theme-text">{model.name}</div>
                      <div className="text-xs text-theme-text-muted">{model.description}</div>
                    </div>
                    {selectedModel === model.id && (
                      <Check className="h-4 w-4 text-accent-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={fetchData} className="btn btn-secondary" disabled={loading}>
            {loading ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
            Reload
          </button>
        </div>

        {/* Extra constraints input */}
        <div className="mb-4">
          <label htmlFor="extra-constraints" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
            Additional constraints to verify (optional)
          </label>
          <textarea
            id="extra-constraints"
            value={extraConstraints}
            onChange={(e) => setExtraConstraints(e.target.value)}
            placeholder="e.g. All buttons must have aria-labels. No inline styles allowed. Images must have alt text."
            rows={2}
            className="w-full rounded-lg border border-theme-border-muted bg-theme-surface px-3 py-2 text-sm text-theme-text placeholder:text-theme-text-muted/50 focus:outline-none focus:ring-2 focus:ring-teal-500/40 resize-y"
          />
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="card p-10 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : projects.length === 0 ? (
          <div className="card p-10 text-center text-theme-text-muted">No built mini-apps found.</div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-theme-surface border-b border-theme-border-muted">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Mini-App
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Last Validation
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => {
                    const validation = validations.get(project.id);
                    const vs = validationStates.get(project.id);
                    const isRunning = vs?.status === 'creating' || vs?.status === 'streaming';

                    return (
                      <tr key={project.id} className="border-b border-theme-border-muted/60 last:border-b-0">
                        {/* Name */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {project.logo_emoji && (
                              <span className="text-lg">{project.logo_emoji}</span>
                            )}
                            <div>
                              <div className="font-medium text-theme-text">{project.name}</div>
                              <div className="text-xs text-theme-text-muted mt-0.5">
                                {project.profiles?.display_name || project.profiles?.email || 'Unknown'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Last Validation Date */}
                        <td className="px-4 py-3 text-sm text-theme-text">
                          {validation ? (
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-theme-text-muted" />
                              {formatDate(validation.validated_at)}
                            </span>
                          ) : (
                            <span className="text-theme-text-muted">Never</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          {isRunning ? (
                            <div className="flex items-center gap-2">
                              <LoadingSpinner size="sm" />
                              <span className="text-xs text-theme-text-muted truncate max-w-[200px]">
                                {vs?.streamingStatus || 'Working...'}
                              </span>
                            </div>
                          ) : vs?.status === 'error' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 border border-red-500/20 px-2 py-0.5 text-xs font-medium text-red-400">
                              Error
                            </span>
                          ) : vs?.status === 'done' && vs.result ? (
                            <StatusBadge result={vs.result} />
                          ) : validation ? (
                            <StatusBadge result={validation.validation_result} />
                          ) : (
                            <span className="text-xs text-theme-text-muted">--</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleRefresh(project.id)}
                              disabled={isRunning}
                              className="inline-flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Refresh code"
                            >
                              <RefreshCw className={`h-4 w-4 ${isRunning ? 'animate-spin' : ''}`} />
                              Refresh
                            </button>

                            {/* View conversation link — opens in new tab to keep admin page streaming */}
                            {(vs?.conversationId || validation?.conversation_id) && (
                              <a
                                href={`/project/${project.id}/conversation/${vs?.conversationId || validation?.conversation_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                                title="View conversation"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View
                              </a>
                            )}
                          </div>

                          {/* Error message */}
                          {vs?.status === 'error' && vs.error && (
                            <div className="mt-1 text-xs text-red-400 max-w-[300px] truncate" title={vs.error}>
                              {vs.error}
                            </div>
                          )}

                          {/* Streaming content preview */}
                          {isRunning && vs?.streamingContent && (
                            <div className="mt-1 text-xs text-theme-text-muted max-w-[300px] truncate" title={vs.streamingContent}>
                              {vs.streamingContent.slice(-120)}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatusBadge({ result }: { result: 'no_change_needed' | 'fixed' }) {
  if (result === 'no_change_needed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-400">
        <CheckCircle className="h-3 w-3" />
        Validated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-400">
      <Wrench className="h-3 w-3" />
      Fixed
    </span>
  );
}
